import { useState, useEffect, useCallback, useMemo } from "react";
import { Button, Form } from "react-bootstrap";
// components
import ModalLayout from "components/modal/modal-layout/ModalLayout";
import BasicPlanFields from "../basic-plan-fields";
import ConfirmModal from "components/confirm-modal";
// constants
import messages from "messages";
// styles
import styles from "./styles.module.scss";
// hooks
import useToggleModal from "hooks/useToggleModal";

type Props = {
  code: ICDCode & { id: string; [key: string]: any };
  value: TreatmentPlan;
  show: boolean;
  onApply: (arg: TreatmentPlan) => void;
  onCancel: () => void;
};

const BasicPlan = ({ code, value, show, onApply, onCancel }: Props) => {
  const [localValue, setLocalValue] = useState(value);
  const hasValue = useMemo(
    () => Object.keys(code.treatment_plan || {}).length > 1,
    [code.treatment_plan],
  );
  useEffect(() => setLocalValue(value), [value]);

  const handleApply = useCallback(
    () => onApply(localValue),
    [onApply, localValue],
  );
  // cancel editor modal
  const {
    open: openPlanModal,
    show: showPlanModal,
    close: closePlanModal,
  } = useToggleModal(false);

  const handleCancel = useCallback(() => {
    const noChange = JSON.stringify(value) === JSON.stringify(localValue);

    if (noChange) {
      return onCancel();
    }

    return openPlanModal();
  }, [value, localValue, openPlanModal, onCancel]);

  return (
    <>
      {/* plan modal */}
      <ConfirmModal
        labelTitle={messages.areYouSure}
        show={showPlanModal}
        onCancel={closePlanModal}
        onConfirm={() => {
          closePlanModal();
          onCancel();
        }}
        children={messages.ifYouClose}
        labelConfirm={messages.continue}
        labelCancel={messages.keepEditing}
      />
      <ModalLayout
        size={"lg"}
        show={show}
        onHide={handleCancel}
        Header={
          <span className={styles["basic-plan__title"]}>
            {hasValue ? messages.editTreatmentPlan : messages.addTreatmentPlan}
          </span>
        }
        headerClass={styles["basic-plan__header"]}
        bodyClass={styles["basic-plan__body"]}
        Footer={
          <>
            <Button
              className={styles["basic-plan__cancel"]}
              size="sm"
              onClick={handleCancel}
              variant={"link"}
            >
              {messages.cancel}
            </Button>

            <Button
              className={styles["basic-plan__apply"]}
              size="sm"
              onClick={handleApply}
              type={"submit"}
            >
              {messages.completeAndSave}
            </Button>
          </>
        }
        footerClass={styles["basic-plan__footer"]}
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            handleApply();
          }}
        >
          <BasicPlanFields
            value={localValue}
            setValue={setLocalValue}
            code={code}
          />
        </Form>
      </ModalLayout>
    </>
  );
};

export default BasicPlan;
